import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  Link,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@/ui';
import { getErrorMessage } from '@/utils/errors';
import { useDisableExperienceTemplate } from '../hooks/useDisableExperienceTemplate';
import {
  type TemplateAccountExperience,
  useTemplateAccountExperiences,
} from '../hooks/useTemplateAccountExperiences';
import { TemplateModal } from './TemplateModal';

export const TemplatesTable = () => {
  const { experiences, isLoading, error } = useTemplateAccountExperiences();
  const { disableExperienceTemplate, isDisabling } =
    useDisableExperienceTemplate();
  const [editing, setEditing] = useState<TemplateAccountExperience | null>(
    null
  );

  if (isLoading) {
    return <Spinner />;
  }
  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {getErrorMessage(error)}
      </Alert>
    );
  }
  if (experiences.length === 0) {
    return (
      <Text color="gray.500">No experiences in the template account.</Text>
    );
  }

  return (
    <>
      <TableContainer>
        <Table colorScheme="gray" size="sm" variant="striped">
          <Thead>
            <Tr>
              <Th>Experience</Th>
              <Th>Template name</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {experiences.map((experience) => {
              const { template } = experience;
              return (
                <Tr key={experience.id}>
                  <Td>
                    <Link
                      as={RouterLink}
                      to={`/experience/${experience.id}/edit`}
                    >
                      {experience.title || 'Untitled'}
                    </Link>
                  </Td>
                  <Td>{template?.name}</Td>
                  <Td textAlign="right">
                    {template ? (
                      <Flex gap={2} justify="flex-end">
                        <Button
                          size="xs"
                          onClick={() => setEditing(experience)}
                        >
                          Edit
                        </Button>
                        <Button
                          isLoading={isDisabling(experience.id)}
                          size="xs"
                          variant="secondary"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Disable the "${template.name}" template? It will no longer be listed on the website.`
                              )
                            ) {
                              disableExperienceTemplate({
                                input: { experienceId: experience.id },
                              });
                            }
                          }}
                        >
                          Disable
                        </Button>
                      </Flex>
                    ) : (
                      <Button size="xs" onClick={() => setEditing(experience)}>
                        Enable
                      </Button>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
      {editing && (
        <TemplateModal
          experience={editing}
          isOpen
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
};
